import { intToFormattedString } from '../lib/util';

/*
 * The decoration fields Collaterate sends on both a project line (OrderItemDTO, mapped by OrderItem)
 * and an order line (RegularOrderItemDTO, mapped by OrderedItem). Each tolerates a missing or
 * malformed value, so a bad payload leaves a field empty instead of throwing in a model constructor.
 */

const NO_CHARGE = 0;
const ONE_TIME = 1;
const CENTS = 2;

export function decorationsOf (decorations) {
    return Object.freeze(arrayOf(decorations).filter(Boolean).map(decorationOf));
}

export function decorationProofsOf (proofs) {
    return proofsOf(proofs, proof => proof && proof.image);
}

/*
 * An order keeps its proofs from checkout, but OrderDecorationProofs#describe sends each without an
 * image until it has been rendered, shortly after; its photo stands in until then.
 */
export function orderedDecorationProofsOf (proofs) {
    return proofsOf(proofs, proof => proof && (proof.image || proof.photo));
}

export function decorationChargesOf (charges) {
    const lines = arrayOf(charges).filter(charge => charge && charge.label);
    const isFirstAtLocation = opensLocation(lines);
    return Object.freeze(lines.map((charge, index) => chargeOf(charge, isFirstAtLocation(charge, index))));
}

export function decorationNoticesOf (notices) {
    return Object.freeze(arrayOf(notices).filter(notice => typeof notice === 'string' && notice.trim()));
}

export function decorationGroupIdOf (data) {
    return data.decorationGroupId || null;
}

export function decorationGroupOf (group) {
    if (!group || !group.id) {
        return null;
    }
    const price = moneyOf(group.price);
    const totalPrice = moneyOf(group.totalPrice);
    return Object.freeze({
        id: group.id,
        orderItemIds: Object.freeze(arrayOf(group.orderItemIds).filter(Number.isInteger)),
        pieces: Number.isInteger(group.pieces) ? group.pieces : null,
        sizes: group.sizes || null,
        price,
        totalPrice,
        displayPrice: displayOf(price),
        displayTotalPrice: displayOf(totalPrice),
        charges: decorationChargesOf(group.charges),
    });
}

export function isDecorationGroupFolded (data) {
    return data.decorationGroupFolded === true;
}

function arrayOf (value) {
    return Array.isArray(value) ? value : [];
}

function moneyOf (amount) {
    return Number.isFinite(amount) ? amount : NO_CHARGE;
}

function displayOf (amount) {
    return intToFormattedString(amount.toFixed(CENTS));
}

function proofsOf (proofs, isShown) {
    const shown = arrayOf(proofs).filter(isShown);
    const flagged = shown.some(proof => proof.isDefault === true);
    const [first] = shown;
    return Object.freeze(shown.map(proof => proofOf(proof, flagged ? proof.isDefault === true : proof === first)));
}

function proofOf (proof, isDefault) {
    const locationIds = locationIdsOf(proof);
    const locationNames = locationNamesOf(proof);
    const [locationId = null] = locationIds;
    const [locationName = ''] = locationNames;
    return Object.freeze({
        photoId: Number.isInteger(proof.photoId) ? proof.photoId : null,
        image: proof.image || null,
        photo: proof.photo || null,
        locationIds: Object.freeze(locationIds),
        locationNames: Object.freeze(locationNames),
        locationId,
        locationName,
        variantMatched: proof.variantMatched !== false,
        variantLabel: proof.variantLabel || null,
        isDefault,
    });
}

function locationIdsOf (proof) {
    return (Array.isArray(proof.locationIds) ? proof.locationIds : [proof.locationId]).filter(Number.isInteger);
}

function locationNamesOf (proof) {
    if (Array.isArray(proof.locationNames)) {
        return proof.locationNames.filter(Boolean);
    }
    return proof.locationName ? [proof.locationName] : [];
}

function decorationOf (decoration) {
    const inkColors = inkColorsOf(decoration.inkColors);
    const locationName = decoration.locationName || '';
    return Object.freeze({
        locationId: decoration.locationId,
        locationName,
        locationDisplayName: decoration.locationDisplayName || locationName,
        decorationId: decoration.decorationId,
        decorationName: decoration.decorationName || '',
        summary: decoration.summary || null,
        brandedLogoFileId: Number.isInteger(decoration.brandedLogoFileId) ? decoration.brandedLogoFileId : null,
        brandedLogoName: decoration.brandedLogoName || null,
        brandedLogoUrl: decoration.brandedLogoUrl || null,
        inkColors,
        inksChosen: typeof decoration.inksChosen === 'boolean'
            ? decoration.inksChosen
            : inkColors.some(color => color.changed),
    });
}

function inkColorsOf (colors) {
    return Object.freeze(arrayOf(colors)
        .filter(color => color && (color.pms || color.name))
        .map(color => Object.freeze({
            pms: color.pms || null,
            name: color.name || null,
            hex: color.hex || null,
            changed: color.changed === true,
        })));
}

/*
 * DecorationCharges sends firstAtLocation true on a location's first line and omits it on the rest,
 * so once any line is flagged an absent one reads as false.
 */
function opensLocation (lines) {
    const flagged = lines.some(line => line.firstAtLocation === true);
    return (charge, index) => (flagged
        ? charge.firstAtLocation === true
        : lines.findIndex(line => line.locationId === charge.locationId) === index);
}

function chargeOf (charge, firstAtLocation) {
    const unitPrice = moneyOf(charge.unitPrice);
    const total = moneyOf(charge.total);
    return Object.freeze({
        locationId: Number.isInteger(charge.locationId) ? charge.locationId : null,
        locationName: charge.locationName || '',
        kind: charge.kind || '',
        label: charge.label,
        quantity: Number.isInteger(charge.quantity) ? charge.quantity : ONE_TIME,
        unitPrice,
        total,
        displayUnitPrice: displayOf(unitPrice),
        displayTotal: displayOf(total),
        description: charge.description || null,
        firstAtLocation,
    });
}
